import { useCallback } from 'react';
import { FullProject, Project, Task } from '../../types';
import { formatDate, parseDate, todayStr } from '../../utils/dateUtils';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { areProjectIdsMatching } from '../../utils/idNormalizer';

export interface UseProjectCRUDProps {
  allProjects: FullProject[];
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  currentProjectId: string;
  setCurrentProjectId: (id: string) => void;
  setProjectName: (name: string) => void;
  activeWorkspace: 'professional' | 'private';
  userId: string | null;
  tenantId: string;
  COMM_PROJECT_ID: string;
  INBOX_PROJECT_ID: string;
  markProjectDirty: (id: string) => void;
  syncProjectToFirestore: (project: FullProject, isBackground?: boolean) => Promise<boolean | void | number | null>;
  deleteProjectFromPersistence: (id: string) => Promise<void>;
}

export function useProjectCRUD({
  allProjects,
  setAllProjects,
  currentProjectId,
  setCurrentProjectId,
  setProjectName,
  activeWorkspace,
  userId,
  tenantId,
  COMM_PROJECT_ID,
  INBOX_PROJECT_ID,
  markProjectDirty,
  syncProjectToFirestore,
  deleteProjectFromPersistence
}: UseProjectCRUDProps) {

  const reorderProjects = useCallback((reorderedList: Project[]) => {
    setAllProjects(prev => {
      // 1. Get all projects in the current workspace (excluding system projects)
      const workspaceProjects = prev.filter(p => {
        const projectWorkspace = p.workspace || 'professional';
        return projectWorkspace === activeWorkspace && p.id !== INBOX_PROJECT_ID && p.id !== COMM_PROJECT_ID;
      });

      // 2. Sort them to get their current layout order
      const sortedWorkspaceProjects = [...workspaceProjects].sort((a, b) => {
        if (a.order !== undefined || b.order !== undefined) {
          const orderA = a.order ?? 9999;
          const orderB = b.order ?? 9999;
          if (orderA !== orderB) return orderA - orderB;
        }
        return (b.lastModified || 0) - (a.lastModified || 0);
      });

      // 3. Create a list of projects that are NOT in the reordered list
      const reorderedIds = new Set(reorderedList.map(p => p.id));
      const remainingProjects = sortedWorkspaceProjects.filter(p => !reorderedIds.has(p.id));

      // 4. Construct the complete new ordered list for this workspace:
      // first the reordered items, then the remaining items
      const newOrderedWorkspaceList = [...reorderedList, ...remainingProjects];

      // 5. Update the orders in the main list
      const updated = prev.map(p => {
        const newIdx = newOrderedWorkspaceList.findIndex(u => u.id === p.id);
        if (newIdx !== -1) {
          // If the order has changed, update it and mark dirty
          if (p.order !== newIdx) {
            markProjectDirty(p.id);
            return { ...p, order: newIdx, lastModified: Date.now() };
          }
        }
        return p;
      });

      return updated;
    });
  }, [activeWorkspace, markProjectDirty, INBOX_PROJECT_ID, COMM_PROJECT_ID, setAllProjects]);

  const addProject = useCallback(async () => {
    const newId = Date.now().toString();
    const colors = [
      // 500s (Balanced)
      'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-pink-500', 'bg-purple-500', 
      'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-teal-500', 'bg-orange-500',
      'bg-lime-500', 'bg-violet-500', 'bg-fuchsia-500', 'bg-sky-500', 'bg-yellow-500', 
      'bg-red-500',
      // 400s (Lighter)
      'bg-blue-400', 'bg-emerald-400', 'bg-amber-400', 'bg-pink-400', 'bg-purple-400',
      'bg-rose-400', 'bg-cyan-400', 'bg-indigo-400', 'bg-teal-400', 'bg-orange-400',
      'bg-lime-400', 'bg-violet-400', 'bg-fuchsia-400', 'bg-sky-400', 'bg-red-400',
      // 600s (Darker)
      'bg-blue-600', 'bg-emerald-600', 'bg-amber-600', 'bg-pink-600', 'bg-purple-600',
      'bg-rose-600', 'bg-cyan-600', 'bg-indigo-600', 'bg-teal-600', 'bg-orange-600',
      'bg-lime-600', 'bg-violet-600', 'bg-fuchsia-600'
    ];
    
    // Find colors not currently in use by non-archived projects
    const usedColors = new Set(allProjects.filter(p => !p.archived).map(p => p.color));
    const availableColors = colors.filter(c => !usedColors.has(c));
    
    // Pick from available colors, or a random one from the full list if all are used
    const pool = availableColors.length > 0 ? availableColors : colors;
    const randomColor = pool[Math.floor(Math.random() * pool.length)];
    const newProject: FullProject = {
      id: newId,
      name: "Neues Projekt",
      tasks: [],
      lastModified: Date.now(),
      color: randomColor,
      workspace: activeWorkspace, // ASSIGN CURRENT WORKSPACE
      userId: userId || 'unknown',
      tenantId: tenantId,
      visibility: 'private',
      metadata: {},
      changelog: []
    };
    
    // M-2: Optimistic update - update local state immediately so UI never blocks
    setAllProjects(prev => {
      if (prev.some(p => p.id === newId)) return prev;
      return [...prev, newProject];
    });
    setCurrentProjectId(newId);
    markProjectDirty(newId);

    void syncProjectToFirestore(newProject).catch(e => {
      console.warn('[Sync] Failed background sync for new project:', e);
    });

    return newProject;
  }, [syncProjectToFirestore, activeWorkspace, allProjects, tenantId, userId, setAllProjects, setCurrentProjectId, markProjectDirty]);

  const archiveProject = useCallback(async (id: string) => {
    setAllProjects(prev => {
      const project = prev.find(p => p.id === id);
      if (!project) return prev;
      const updated = { ...project, archived: true, lastModified: Date.now() };
      return prev.map(p => p.id === id ? updated : p);
    });
    markProjectDirty(id);
  }, [markProjectDirty, setAllProjects]);

  const unarchiveProject = useCallback(async (id: string) => {
    setAllProjects(prev => {
      const project = prev.find(p => p.id === id);
      if (!project) return prev;
      const updated = { ...project, archived: false, lastModified: Date.now() };
      return prev.map(p => p.id === id ? updated : p);
    });
    markProjectDirty(id);
  }, [markProjectDirty, setAllProjects]);

  const confirmDuplicateProject = useCallback(async (
    sourceId: string, 
    duplicateReference: 'start' | 'end' | 'milestone', 
    duplicateDate: string, 
    duplicateMilestoneId: string | number | null,
    newName?: string
  ) => {
    const project = allProjects.find(p => p.id === sourceId);
    if (!project) return;

    const targetDate = new Date(duplicateDate);
    if (isNaN(targetDate.getTime())) return;

    let offsetMs = 0;
    if (project.tasks.length > 0) {
      if (duplicateReference === 'start') {
        const earliestStart = new Date(Math.min(...project.tasks.map(t => parseDate(t.start || todayStr).getTime())));
        offsetMs = targetDate.getTime() - earliestStart.getTime();
      } else if (duplicateReference === 'end') {
        const latestEnd = new Date(Math.max(...project.tasks.map(t => parseDate(t.end || todayStr).getTime())));
        offsetMs = targetDate.getTime() - latestEnd.getTime();
      } else if (duplicateReference === 'milestone' && duplicateMilestoneId) {
        const milestone = project.tasks.find(t => t.id.toString() === duplicateMilestoneId.toString());
        if (milestone) {
          const milestoneDate = parseDate(milestone.start || todayStr);
          offsetMs = targetDate.getTime() - milestoneDate.getTime();
        }
      }
    }

    // Map old IDs to new IDs to maintain dependencies and parent-child relationships
    const idMap = new Map<string | number, string>();
    project.tasks.forEach(t => {
      idMap.set(t.id, Date.now().toString() + Math.random().toString(36).substring(2, 9));
    });

    const newId = Date.now().toString();
    const newTasks: Task[] = project.tasks.map(t => {
      const oldStart = parseDate(t.start || todayStr);
      const oldEnd = parseDate(t.end || todayStr);
      const newStart = new Date(oldStart.getTime() + offsetMs);
      const newEnd = new Date(oldEnd.getTime() + offsetMs);

      return {
        ...t,
        id: idMap.get(t.id)!,
        projectId: newId, // M-3: Retarget to the duplicated project ID
        userId: userId || undefined, // M-3: Set to the current duplicating user
        tenantId: tenantId || project.tenantId,
        parentId: t.parentId ? (idMap.get(t.parentId) || t.parentId) : undefined,
        dependencies: (t.dependencies || []).map(d => idMap.get(d) || d),
        start: formatDate(newStart),
        end: formatDate(newEnd),
        completed: false,
        status: 'open',
        assignee: '',
        notes: '',
        subtitle: '',
        isInlineNew: false,
        trackedTime: 0, // M-3: Reset tracked time
        timerStartedAt: null, // M-3: Reset running timer
      };
    });

    const cleanMetadata = { ...(project.metadata || {}) };
    delete cleanMetadata.isSample; // M-3: Strip sample flag from metadata

    const newProject: FullProject = {
      ...project,
      id: newId,
      name: newName || `${project.name} (Kopie)`,
      tasks: newTasks,
      archived: false,
      lastModified: Date.now(),
      workspace: activeWorkspace, // M-3: Place duplicated project into active workspace
      userId: userId || 'unknown', // M-3: Current user becomes sole owner
      tenantId: tenantId || project.tenantId,
      visibility: 'private', // M-3: Reset visibility to private
      sharedWith: [], // M-3: Strip foreign sharedWith list
      sharedEditors: [], // M-3: Strip foreign sharedEditors list
      invitations: {}, // M-3: Clear any pending invitations
      isSample: false, // M-3: Not a sample project
      metadata: cleanMetadata,
      changelog: [],
    };

    // M-2: Optimistic update - update local state immediately, then sync to Firestore in background
    setAllProjects(prev => {
      if (prev.some(p => p.id === newId)) return prev;
      return [...prev, newProject];
    });
    setCurrentProjectId(newId);
    markProjectDirty(newId);

    void syncProjectToFirestore(newProject).catch(e => {
      console.warn('[Sync] Failed background sync for duplicated project:', e);
    });

    return newProject;
  }, [allProjects, syncProjectToFirestore, setAllProjects, setCurrentProjectId, markProjectDirty, activeWorkspace, userId, tenantId]);

  const deleteProject = useCallback(async (id: string) => {
    // If we're deleting a sample project, mark it as dismissed in the inbox metadata
    const proj = allProjects.find(p => p.id === id);
    if (proj && (proj.isSample || proj.metadata?.isSample)) {
      const inboxProject = allProjects.find(p => p.id === INBOX_PROJECT_ID);
      if (inboxProject) {
        const updatedInbox = {
          ...inboxProject,
          metadata: {
            ...(inboxProject.metadata || {}),
            sampleDismissed: true
          },
          lastModified: Date.now()
        };
        setAllProjects(prev => prev.map(p => p.id === INBOX_PROJECT_ID ? updatedInbox : p));
        try {
          await updateDoc(doc(db, 'projects', INBOX_PROJECT_ID), {
            'metadata.sampleDismissed': true,
            lastModified: Date.now()
          });
        } catch (e) {
          console.warn('[Sync] Failed to dismiss sample project on delete:', e);
        }
      }
    }

    if (allProjects.length <= 1) return;
    
    // UI Feedback: Immediately remove from local state
    setAllProjects(prev => {
      const filtered = prev.filter(p => p.id !== id);
      if (areProjectIdsMatching(currentProjectId, id)) {
        const nextProject = filtered[0];
        if (nextProject) {
          setCurrentProjectId(nextProject.id);
        }
      }
      return filtered;
    });

    // Background sync: Purge from databases
    await deleteProjectFromPersistence(id);
  }, [allProjects, currentProjectId, deleteProjectFromPersistence, setAllProjects, setCurrentProjectId, INBOX_PROJECT_ID]);

  const renameProject = useCallback(async (id: string, newName: string, newColor?: string, newVisibility?: 'private' | 'public') => {
    setAllProjects(prev => {
      const project = prev.find(p => p.id === id);
      if (!project) return prev;
      const updated = { ...project, 
        name: newName, 
        color: newColor || project.color, 
        visibility: newVisibility || project.visibility || 'private',
        lastModified: Date.now() 
      };
      return prev.map(p => p.id === id ? updated : p);
    });

    markProjectDirty(id);
    if (areProjectIdsMatching(id, currentProjectId)) {
      setProjectName(newName);
    }
  }, [currentProjectId, markProjectDirty, setAllProjects, setProjectName]);

  return {
    reorderProjects,
    addProject,
    archiveProject,
    unarchiveProject,
    confirmDuplicateProject,
    deleteProject,
    renameProject
  };
}
